/**
 * RepConnect - Admin Delegation System
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * This software and associated documentation files are the proprietary
 * property of RepConnect, LLC.
 * All rights reserved.
 */

import { UserStorage } from './userStorage';
import { MeetingStorage } from './meetingStorage';
import emailService from './emailService';
import { logger } from '../utils/logger';

export class AdminDelegation {
  
  static ADMIN_ROLES = {
    OFFICE_MANAGER: 'office_manager',
    LEAD_MEDICAL_ASSISTANT: 'lead_medical_assistant', 
    PRACTICE_COORDINATOR: 'practice_coordinator',
    SCHEDULER: 'scheduler'
  };

  static PERMISSIONS = {
    MANAGE_MEETINGS: 'manage_meetings',
    MANAGE_AVAILABILITY: 'manage_availability',
    MANAGE_PREFERENCES: 'manage_preferences',
    MANAGE_STAFF: 'manage_staff',
    VIEW_REPORTS: 'view_reports',
    APPROVE_MEETINGS: 'approve_meetings',
    MANAGE_REPS: 'manage_reps'
  };

  /**
   * Get default permissions for admin roles
   */
  static getDefaultPermissions(role) {
    const rolePermissions = {
      [this.ADMIN_ROLES.OFFICE_MANAGER]: [
        this.PERMISSIONS.MANAGE_MEETINGS,
        this.PERMISSIONS.MANAGE_AVAILABILITY,
        this.PERMISSIONS.MANAGE_PREFERENCES,
        this.PERMISSIONS.MANAGE_STAFF,
        this.PERMISSIONS.VIEW_REPORTS,
        this.PERMISSIONS.APPROVE_MEETINGS,
        this.PERMISSIONS.MANAGE_REPS
      ],
      [this.ADMIN_ROLES.LEAD_MEDICAL_ASSISTANT]: [
        this.PERMISSIONS.MANAGE_MEETINGS,
        this.PERMISSIONS.MANAGE_AVAILABILITY,
        this.PERMISSIONS.APPROVE_MEETINGS,
        this.PERMISSIONS.VIEW_REPORTS
      ],
      [this.ADMIN_ROLES.PRACTICE_COORDINATOR]: [
        this.PERMISSIONS.MANAGE_MEETINGS,
        this.PERMISSIONS.MANAGE_AVAILABILITY,
        this.PERMISSIONS.MANAGE_PREFERENCES,
        this.PERMISSIONS.APPROVE_MEETINGS,
        this.PERMISSIONS.MANAGE_REPS
      ],
      [this.ADMIN_ROLES.SCHEDULER]: [
        this.PERMISSIONS.MANAGE_MEETINGS,
        this.PERMISSIONS.MANAGE_AVAILABILITY,
        this.PERMISSIONS.APPROVE_MEETINGS
      ]
    };

    return rolePermissions[role] || [];
  }

  /**
   * Create a new admin user for a physician
   */
  static createAdminUser(physicianId, adminData) {
    try {
      const adminId = `admin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const currentAdmins = this.getAdminUsers(physicianId);
      
      const newAdmin = {
        id: adminId,
        physicianId: physicianId,
        name: adminData.name,
        email: adminData.email,
        phone: adminData.phone || '',
        role: adminData.role,
        title: adminData.title || this.getRoleDisplayName(adminData.role),
        permissions: adminData.permissions || this.getDefaultPermissions(adminData.role),
        isActive: true,
        inviteStatus: 'pending', // 'pending', 'accepted', 'expired'
        inviteToken: this.generateInviteToken(),
        invitedAt: new Date().toISOString(),
        invitedBy: adminData.invitedBy,
        createdAt: new Date().toISOString(),
        lastLoginAt: null,
        preferences: {
          emailNotifications: true,
          smsNotifications: false,
          weeklyReports: true
        }
      };

      currentAdmins.push(newAdmin);
      localStorage.setItem(`physician_admins_${physicianId}`, JSON.stringify(currentAdmins));
      
      // Send invite email asynchronously
      this.sendInviteEmail(newAdmin).catch(error => {
        logger.error('Failed to send invite email', { error: error.message });
      });
      
      return newAdmin;
    } catch (error) {
      logger.error('Error creating admin user', { error: error.message });
      return null;
    }
  }

  /**
   * Get all admin users for a physician
   */
  static getAdminUsers(physicianId) {
    try {
      const adminsData = localStorage.getItem(`physician_admins_${physicianId}`);
      return adminsData ? JSON.parse(adminsData) : [];
    } catch (error) {
      logger.error('Error loading admin users', { error: error.message });
      return [];
    }
  }

  /**
   * Get active admin users only
   */
  static getActiveAdminUsers(physicianId) {
    return this.getAdminUsers(physicianId).filter(admin => admin.isActive);
  }

  /**
   * Update admin user
   */
  static updateAdminUser(physicianId, adminId, updateData) {
    try {
      const admins = this.getAdminUsers(physicianId);
      const adminIndex = admins.findIndex(admin => admin.id === adminId);
      
      if (adminIndex === -1) return null;
      
      admins[adminIndex] = {
        ...admins[adminIndex],
        ...updateData,
        updatedAt: new Date().toISOString()
      };
      
      localStorage.setItem(`physician_admins_${physicianId}`, JSON.stringify(admins));
      return admins[adminIndex];
    } catch (error) {
      logger.error('Error updating admin user', { error: error.message });
      return null;
    }
  }

  /**
   * Deactivate admin user
   */
  static deactivateAdminUser(physicianId, adminId) {
    return this.updateAdminUser(physicianId, adminId, {
      isActive: false,
      deactivatedAt: new Date().toISOString()
    });
  }

  /**
   * Delete admin user permanently
   */
  static deleteAdminUser(physicianId, adminId) {
    try {
      const admins = this.getAdminUsers(physicianId);
      const filteredAdmins = admins.filter(admin => admin.id !== adminId);
      
      localStorage.setItem(`physician_admins_${physicianId}`, JSON.stringify(filteredAdmins));
      
      // Log the deletion activity
      this.logAdminActivity(physicianId, adminId, 'admin_deleted', {
        deletedAt: new Date().toISOString()
      });
      
      return true;
    } catch (error) {
      logger.error('Error deleting admin user', { error: error.message });
      return false;
    }
  }

  /**
   * Check if user has specific permission
   */
  static hasPermission(adminUser, permission) {
    return adminUser.permissions && adminUser.permissions.includes(permission);
  }

  /**
   * Check if admin can perform action on behalf of physician
   */
  static canActAsPhysician(adminId, physicianId) {
    const admins = this.getAdminUsers(physicianId);
    const admin = admins.find(a => a.id === adminId && a.isActive && a.inviteStatus === 'accepted');
    return !!admin;
  }

  /**
   * Get admin user by ID
   */
  static getAdminUser(physicianId, adminId) {
    const admins = this.getAdminUsers(physicianId);
    return admins.find(admin => admin.id === adminId);
  }

  /**
   * Accept admin invitation
   */
  static acceptInvitation(inviteToken, userData) {
    try {
      // Search through all physicians for this invite token
      const allPhysicians = UserStorage.getAllUsers().filter(u => u.userType === 'customer');
      
      for (const physician of allPhysicians) {
        const admins = this.getAdminUsers(physician.id);
        const admin = admins.find(a => a.inviteToken === inviteToken && a.inviteStatus === 'pending');
        
        if (admin) {
          // Create user account
          const adminUserId = UserStorage.createUser({
            email: admin.email,
            name: admin.name,
            userType: 'admin',
            adminRole: admin.role,
            physicianId: physician.id,
            title: admin.title,
            phone: userData.phone || admin.phone,
            onboardingCompleted: true
          });

          if (adminUserId) {
            // Update admin record
            this.updateAdminUser(physician.id, admin.id, {
              inviteStatus: 'accepted',
              acceptedAt: new Date().toISOString(),
              userId: adminUserId,
              lastLoginAt: new Date().toISOString()
            });

            return {
              admin: this.getAdminUser(physician.id, admin.id),
              physician: physician,
              userId: adminUserId
            };
          }
        }
      }
      
      return null;
    } catch (error) {
      logger.error('Error accepting invitation', { error: error.message });
      return null;
    }
  }

  /**
   * Generate invite token
   */
  static generateInviteToken() {
    return `invite_${Date.now()}_${Math.random().toString(36).substr(2, 20)}`;
  }

  /**
   * Get role display name
   */
  static getRoleDisplayName(role) {
    const roleNames = {
      [this.ADMIN_ROLES.OFFICE_MANAGER]: 'Office Manager',
      [this.ADMIN_ROLES.LEAD_MEDICAL_ASSISTANT]: 'Lead Medical Assistant',
      [this.ADMIN_ROLES.PRACTICE_COORDINATOR]: 'Practice Coordinator',
      [this.ADMIN_ROLES.SCHEDULER]: 'Scheduler'
    };
    
    return roleNames[role] || role;
  }

  /**
   * Send invite email using EmailService
   */
  static async sendInviteEmail(adminUser) {
    try {
      const inviteUrl = `${window.location.origin}/admin-invite/${adminUser.inviteToken}`;
      
      // Get physician info for the email
      const physician = UserStorage.getUserById(adminUser.physicianId);
      const physicianName = physician ? (physician.fullName || physician.name || 'the physician') : 'the physician';
      
      logger.debug('📧 Sending admin invitation email...');
      logger.debug('  To:', adminUser.email);
      logger.debug('  For:', physicianName);
      logger.debug('  Invite URL:', inviteUrl);
      
      const result = await emailService.sendAdminInvite(adminUser, physicianName, inviteUrl);
      
      if (result.success) {
        logger.debug('✅ Admin invitation email sent successfully');
        return { success: true, messageId: result.messageId };
      } else {
        logger.error('Failed to send admin invitation email', { error: result.error });
        return { success: false, error: result.error };
      }
    } catch (error) {
      logger.error('Error sending admin invitation email', { error: error.message });
      return { success: false, error: error.message };
    }
  }

  /**
   * Get admin dashboard data
   */
  static getAdminDashboardData(physicianId) {
    const physician = UserStorage.getUserById(physicianId);
    const meetings = MeetingStorage.getMeetingsForUser(physicianId, 'customer');
    const pendingRequests = meetings.filter(m => m.status === 'pending');
    const upcomingMeetings = meetings.filter(m => {
      const meetingDate = new Date(m.date);
      return meetingDate >= new Date() && m.status === 'confirmed';
    });

    return {
      physician,
      totalMeetings: meetings.length,
      pendingRequests: pendingRequests.length,
      upcomingMeetings: upcomingMeetings.length,
      recentActivity: this.getRecentActivity(physicianId),
      adminUsers: this.getActiveAdminUsers(physicianId)
    };
  }

  /**
   * Get recent activity for admin dashboard
   */
  static getRecentActivity(physicianId) {
    // Mock recent activity data
    return [
      {
        id: 1,
        type: 'meeting_request',
        message: 'New meeting request from John Smith (PharmaCorp)',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        priority: 'medium'
      },
      {
        id: 2,
        type: 'meeting_confirmed',
        message: 'Meeting confirmed with Sarah Johnson for tomorrow at 12:30 PM',
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
        priority: 'low'
      },
      {
        id: 3,
        type: 'availability_updated',
        message: 'Schedule updated for next week',
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        priority: 'low'
      }
    ];
  }

  /**
   * Get admin activity log
   */
  static logAdminActivity(physicianId, adminId, action, details) {
    try {
      const activityKey = `admin_activity_${physicianId}`;
      const existingActivity = JSON.parse(localStorage.getItem(activityKey) || '[]');
      
      const newActivity = {
        id: Date.now(),
        adminId,
        action,
        details,
        timestamp: new Date().toISOString()
      };
      
      existingActivity.unshift(newActivity);
      
      // Keep only last 100 activities
      const trimmedActivity = existingActivity.slice(0, 100);
      localStorage.setItem(activityKey, JSON.stringify(trimmedActivity));
      
      return newActivity;
    } catch (error) {
      logger.error('Error logging admin activity', { error: error.message });
      return null;
    }
  }

  /**
   * Get admin activity history
   */
  static getAdminActivityHistory(physicianId) {
    try {
      const activityKey = `admin_activity_${physicianId}`;
      return JSON.parse(localStorage.getItem(activityKey) || '[]');
    } catch (error) {
      logger.error('Error getting admin activity history', { error: error.message });
      return [];
    }
  }
}

export default AdminDelegation;